import { useParams } from 'react-router';
import { CONNECTOR_OAUTH_CALLBACK_PATH } from '@bower/shared/connectors/types';

/**
 * Single source of truth for every client route.
 *
 * Each param route is declared ONCE via `paramRoute(pattern, build)`, yielding
 * both the `<Route path>` pattern and a typed builder used by
 * `navigate()` / `<Link to>`. Static routes are plain string literals. This
 * keeps the pattern and the navigation path from drifting apart.
 *
 * Adopt everywhere — a scoped ESLint rule bans literal absolute paths in
 * navigation APIs so new code can't reintroduce stringly-typed paths.
 */

type ParamRouteFn<Args extends readonly string[]> = ((...args: Args) => string) & {
  readonly pattern: string;
};

function paramRoute<Args extends readonly string[]>(
  pattern: string,
  build: (...args: Args) => string,
): ParamRouteFn<Args> {
  return Object.assign(build, { pattern });
}

export const ROUTES = {
  // --- Static ---
  root: '/',
  dashboard: '/dashboard',
  onboarding: '/onboarding',
  collections: '/collections',
  artifacts: '/artifacts',
  captureStream: '/capture-stream',
  protocols: '/protocols',
  equipment: '/equipment',
  graph: '/graph',
  trash: '/trash',
  audit: '/audit',
  settings: '/settings',
  // Connector OAuth return landing. Rendered outside ProtectedRoute so a
  // mid-onboarding user isn't bounced. Shared SSOT with the backend's
  // `resolveCallbackUrl` in `composio.gateway.ts` — renaming either side
  // alone would strand the OAuth popup on the SPA catch-all.
  connectorCallback: CONNECTOR_OAUTH_CALLBACK_PATH,
  account: '/account',
  admin: '/admin',
  liveSession: '/live/session',
  // Internal preview of a LiveKit call (gated by `live-call-preview-enabled`):
  // joins the room the mobile app's Call tab uses, no backend rooms yet.
  liveCall: '/live/call',
  // Route-less nav target: no <Route> in App.tsx — kept as a nav-target
  // constant so the ESLint guard passes; navigating here falls through to
  // NotFound today (pre-existing dead-click the migration preserved).
  // Tracked in issue #289: wire to the correct in-app action (expand
  // sidebar) and then remove so ROUTES holds only routable paths.
  // (`chat` was removed 2026-07: there is no full-page chat — every chat
  // entry point opens the Bird panel via the `chat:openPanel` event.)
  sharedWithMe: '/shared-with-me',
  // --- Auth ---
  auth: '/auth',
  resetPassword: '/reset-password',
  authAction: '/auth/action',
  verifyEmail: '/verify-email',
  verifyEmailPrompt: '/verify-email-prompt',
  recoverEmail: '/recover-email',
  authWorkos: '/auth/workos',
  login: '/login',
  // --- Mobile ---
  mobileCapture: '/m/capture',
  mobileBird: '/m/bird',
  mobileWorkspace: '/m/workspace',
  mobileLive: '/m/live',
  mobileLiveSession: '/m/live/session',
  mobileNotes: '/m/notes',
  // --- Legacy static redirect source ---
  projects: '/projects',
  // --- Param routes ---
  collection: paramRoute(
    '/collections/:projectId',
    (projectId: string) => `/collections/${projectId}`,
  ),
  collectionSingular: paramRoute(
    '/collection/:projectId',
    (projectId: string) => `/collection/${projectId}`,
  ),
  note: paramRoute('/notes/:noteId', (noteId: string) => `/notes/${noteId}`),
  // The structured protocol page (phase 3 of the protocol-object work). A
  // protocol is an Artifact, so `:protocolId` is an artifact id. The page
  // itself decides what to show by asking whether a ProtocolVersion row
  // exists: the steps, or an import's progress when there is none yet.
  protocol: paramRoute(
    '/protocols/:protocolId',
    (protocolId: string) => `/protocols/${protocolId}`,
  ),
  // One run of a protocol, its run record (spec 2026-10-15 protocol runs
  // layout).
  protocolRun: paramRoute(
    '/protocols/:protocolId/runs/:runId',
    (protocolId: string, runId: string) => `/protocols/${protocolId}/runs/${runId}`,
  ),
  // A native spreadsheet (Univer Pro grid). The registry's `detailRoutePath`
  // for `spreadsheet` names this pattern.
  spreadsheet: paramRoute(
    '/spreadsheets/:spreadsheetId',
    (spreadsheetId: string) => `/spreadsheets/${spreadsheetId}`,
  ),
  // A link artifact (docs/planning/link-artifacts.md, D6): loads the link and
  // replaces itself with the outside URL, for surfaces that hold only an id.
  link: paramRoute('/links/:linkId', (linkId: string) => `/links/${linkId}`),
  attachment: paramRoute(
    '/attachments/:attachmentId',
    (attachmentId: string) => `/attachments/${attachmentId}`,
  ),
  equipmentDetail: paramRoute('/equipment/:id', (id: string) => `/equipment/${id}`),
  share: paramRoute('/share/:token', (token: string) => `/share/${token}`),
  // --- Legacy param redirect source patterns ---
  projectsLegacy: paramRoute(
    '/projects/:projectId',
    (projectId: string) => `/projects/${projectId}`,
  ),
  projectLegacy: paramRoute('/project/:projectId', (projectId: string) => `/project/${projectId}`),
  samplesLegacy: paramRoute('/samples/:sampleId', (sampleId: string) => `/samples/${sampleId}`),
  mobileNoteLegacy: paramRoute('/m/notes/:noteId', (noteId: string) => `/m/notes/${noteId}`),
  mobileAttachmentLegacy: paramRoute(
    '/m/attachments/:attachmentId',
    (attachmentId: string) => `/m/attachments/${attachmentId}`,
  ),
} as const;

/**
 * Param shapes per route key — drives `useRouteParams` return typing.
 * Kept in sync with the `paramRoute` declarations above by hand: TypeScript
 * can't derive per-param-name shapes from the builder signatures without
 * inference gymnastics that aren't warranted here.
 */
export interface RouteParamMap {
  collection: { projectId: string };
  collectionSingular: { projectId: string };
  note: { noteId: string };
  attachment: { attachmentId: string };
  spreadsheet: { spreadsheetId: string };
  link: { linkId: string };
  equipmentDetail: { id: string };
  share: { token: string };
  projectsLegacy: { projectId: string };
  projectLegacy: { projectId: string };
  samplesLegacy: { sampleId: string };
  mobileNoteLegacy: { noteId: string };
  mobileAttachmentLegacy: { attachmentId: string };
}

/**
 * Typed `useParams` for a known route. The `_route` key selects the param shape
 * at compile time (it is unused at runtime — values come from the router);
 * catches typo'd / renamed param keys that a bare `useParams()` would silently
 * return undefined for.
 *
 * Note: values are typed as non-optional `string`, matching the existing
 * `useParams<{ id: string }>()` convention in this codebase. React Router
 * params are `string | undefined` at runtime when read outside a matched route
 * — guard at the call site if that's possible for your component.
 */
export function useRouteParams<K extends keyof RouteParamMap>(_route: K): RouteParamMap[K] {
  return useParams() as RouteParamMap[K];
}
